import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FabricImage } from "@/components/FabricImage";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { UploadCloud, X, Crop } from "lucide-react";
import { ImageEditor } from "@/components/ImageEditor";
import { compressImage } from "@/lib/compressImage";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

export const FabricForm = ({ open, onOpenChange, editItem, onSaved }) => {
  const [colour, setColour] = useState("");
  const [notes, setNotes] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !editItem) return;
    setColour(editItem.colour || "");
    setNotes(editItem.notes || "");
    setImagePath(editItem.image_path || null);
  }, [open, editItem]);

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd);
      setImagePath(data.image_path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/fabrics/${editItem.id}`, { colour, notes, image_path: imagePath });
      toast.success("Stock item updated");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fabric-form-dialog" className="left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 p-0 sm:max-w-none sm:rounded-none lg:w-[calc(100vw-var(--sb-w))]">
        <div className="mx-auto w-full max-w-xl px-4 py-8 sm:px-8">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">Photo, colour & notes</DialogTitle>
          <DialogDescription className="sr-only">Upload a photo for this shade and edit colour or notes</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs">
            <p className="font-head text-sm font-bold tracking-tight">{editItem?.quality_name}</p>
            <p className="mt-0.5 font-mono-num text-zinc-500">
              {editItem?.shade_number ? `Shade ${editItem.shade_number}` : "No shade"}
              {editItem?.company_name ? ` · ${editItem.company_name}` : ""}
            </p>
          </div>

          <div>
            <L>Photo</L>
            <div
              data-testid="fabric-image-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickImage(e.dataTransfer.files?.[0]);
              }}
              className="mt-2 cursor-pointer border-2 border-dashed border-zinc-300 p-4 text-center transition-colors duration-200 hover:border-zinc-950"
            >
              {imagePath ? (
                <div className="relative">
                  <FabricImage path={imagePath} alt="Shade" className="mx-auto h-28 w-full max-w-xs object-cover" />
                  <button
                    type="button"
                    data-testid="fabric-image-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditorOpen(true);
                    }}
                    className="absolute left-0 top-0 flex h-6 items-center gap-1 border border-zinc-300 bg-white px-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 transition-colors duration-200 hover:border-zinc-950 hover:text-zinc-950"
                  >
                    <Crop size={11} /> Rotate / crop
                  </button>
                  <button
                    type="button"
                    data-testid="fabric-image-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagePath(null);
                    }}
                    className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center border border-zinc-300 bg-white text-zinc-500 transition-colors duration-200 hover:border-red-500 hover:text-red-600"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div className="py-3 text-zinc-500">
                  <UploadCloud className="mx-auto mb-1.5" size={20} />
                  <p className="text-sm">{uploading ? "Uploading…" : "Click or drag this shade's photo here"}</p>
                </div>
              )}
              <input
                data-testid="fabric-image-input"
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickImage(e.target.files?.[0])}
              />
            </div>
          </div>

          <div>
            <L>Colour</L>
            <Input
              data-testid="fabric-colour-input"
              className="mt-2 rounded-sm"
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              placeholder="e.g. Maroon"
            />
          </div>

          <div>
            <L>Notes</L>
            <Textarea
              data-testid="fabric-notes-input"
              className="mt-2 rounded-sm"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Width, GSM, remarks…"
            />
          </div>

          <p className="border-l-2 border-[#C8A03C] bg-amber-50/60 p-2 text-xs text-zinc-600">
            Name, company, units and prices are managed in <strong>Admin → Item Master</strong>. Stock changes only
            through <strong>Purchase</strong>, <strong>Sale</strong> or <strong>Stock Adjustment</strong>.
          </p>

          <Button
            data-testid="fabric-save-button"
            type="submit"
            disabled={saving}
            className="w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </form>
        </div>
      </DialogContent>
    </Dialog>
      <ImageEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        path={imagePath}
        onSaved={(p) => setImagePath(p)}
      />
    </>
  );
};
